import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { MatSidenavContainer } from '@angular/material/sidenav';

/**
 * Fixes Angular Material sidenav autosize being out of sync when the drawer width
 * is changed via CSS (e.g. transition between expanded/collapsed).
 * 
 * Issue: https://github.com/angular/components/issues/9837
 *
 * Autosize uses a debounced measure that can read the drawer width at the wrong time
 * (e.g. mid-transition), causing the content margin to be wrong or inverted. This
 * directive observes the drawer element and calls updateContentMargins() whenever
 * its size changes so the content area stays in sync.
 *
 * Use on the same mat-sidenav that has a dynamic width (e.g. CSS classes that
 * change width). Pass the container reference: [appSidenavAutosizeFix]="containerRef".
 * Keep [autosize]="true" on mat-sidenav-container.
 */
@Directive({
  selector: 'mat-sidenav[appSidenavAutosizeFix]',
  standalone: true,
})
export class SidenavAutosizeFixDirective implements AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);

  /** Reference to the mat-sidenav-container from parent template. */
  readonly appSidenavAutosizeFix = input.required<MatSidenavContainer>();

  private resizeObserver: ResizeObserver | null = null;

  ngAfterViewInit(): void {
    const containerRef = this.appSidenavAutosizeFix();
    if (!containerRef) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.appSidenavAutosizeFix()?.updateContentMargins());
    });
    this.resizeObserver.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
